"use client";

import { useEffect, useState } from "react";
import { BatchProgress } from "@/components/batch-progress";
import { StoreCombobox } from "@/components/store-combobox";
import { UploadDropzone } from "@/components/upload-dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { HanaroStore } from "@/lib/hanaro-api";
import { toastError, toastSuccess } from "@/lib/toast-helpers";

export default function UploadPage() {
  const [stores, setStores] = useState<HanaroStore[]>([]);
  const [storeId, setStoreId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState<number | null>(null);
  const [batchFiles, setBatchFiles] = useState<
    Array<{
      id: number;
      filename: string;
      status: string;
      invoice_id: number | null;
      error_message: string | null;
    }>
  >([]);
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0 });

  useEffect(() => {
    void (async () => {
      const r = await fetch("/api/stores", { credentials: "include" });
      const j = (await r.json()) as { stores?: HanaroStore[] };
      setStores(j.stores ?? []);
    })();
  }, []);

  useEffect(() => {
    if (!batchId) return;
    const interval = setInterval(() => {
      void (async () => {
        const r = await fetch(`/api/invoices/batch/${batchId}`, { credentials: "include" });
        const j = (await r.json()) as {
          batch?: { completed_files: number; total_files: number; status: string };
          files?: typeof batchFiles;
        };
        if (j.files) setBatchFiles(j.files);
        if (j.batch) {
          setBatchProgress({
            completed: j.batch.completed_files,
            total: j.batch.total_files,
          });
          if (j.batch.status === "completed") {
            clearInterval(interval);
            toastSuccess("Batch processing complete");
          }
        }
      })();
    }, 2000);
    return () => clearInterval(interval);
  }, [batchId]);

  const handleUpload = async () => {
    if (!storeId) {
      toastError("Select a store first");
      return;
    }
    if (files.length === 0) {
      toastError("Add at least one file");
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.set("storeId", storeId);
      for (const f of files) form.append("files", f);

      const r = await fetch("/api/invoices/batch", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const j = (await r.json()) as { batchId?: number; error?: string };
      if (!r.ok) {
        toastError(j.error ?? "Upload failed");
        return;
      }
      setBatchId(j.batchId ?? null);
      setBatchProgress({ completed: 0, total: files.length });
      setFiles([]);
      toastSuccess("Upload started — processing in background");
    } catch {
      toastError("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Upload invoices</h1>
        <p className="text-sm text-muted-foreground">
          Select a store, upload multiple invoices, and auto-match products.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Select store</CardTitle>
          <CardDescription>Invoices will be matched against this store&apos;s product catalog</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Store</Label>
            <StoreCombobox
              stores={stores}
              value={storeId}
              onValueChange={setStoreId}
              disabled={uploading}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Upload files</CardTitle>
          <CardDescription>PDF, JPG, PNG, or CSV — up to 20 files</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadDropzone files={files} onFilesChange={setFiles} disabled={uploading} />
          <Button onClick={() => void handleUpload()} disabled={uploading || !storeId || files.length === 0}>
            {uploading ? "Uploading..." : `Process ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
          </Button>
        </CardContent>
      </Card>

      {batchId && batchFiles.length > 0 && (
        <BatchProgress
          batchId={batchId}
          files={batchFiles}
          completed={batchProgress.completed}
          total={batchProgress.total}
        />
      )}
    </div>
  );
}
